import { CSSProperties } from "react";

import { Hit } from "instantsearch.js";

import { useCurrentUser } from "@/components/helpers/custom-hooks/use-current-user";
import { useWorkspace } from "@/components/helpers/custom-hooks/use-workspace";
import { buildUserGroupFilters } from "@/components/helpers/search/userAndGroupSearchFilter";
import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import {
  AlgoliaMultiselect,
  AlgoliaMultiselectProps,
} from "@/components/pieces/algolia/AlgoliaMultiselect";
import {
  USER_INDEX,
  WORKSPACE_GROUP_INDEX,
} from "@/components/pieces/algolia/indexes";
import { EmailStatus } from "@/components/pieces/form/EmailMultiselect";

import { SuggestionItem } from "./SuggestionItem";

export type UnifiedSearchPropsType = {
  onInputChange?: (value: string) => void;
  canBeInvitedEmails: string[];
  existedEmails: string[];
  pendingInvEmails: string[];
  invalidEmails?: string[];
  existedProgramUserEmails?: string[];
  programId?: string;
  inputStyle?: CSSProperties;
} & Omit<
  AlgoliaMultiselectProps,
  | "indexName"
  | "filters"
  | "renderSuggestion"
  | "getSuggestionValue"
  | "suggestionConverter"
  | "onCreateArtificialObject"
>;

export function UnifiedSearch({
  onInputChange = () => {},
  selections = [],
  canBeInvitedEmails = [],
  existedEmails = [],
  pendingInvEmails = [],
  invalidEmails = [],
  existedProgramUserEmails = [],
  programId,
  onChange,
  placeholder,
  inputStyle,
}: UnifiedSearchPropsType) {
  const getSuggestionStatus = (suggestion: Hit) =>
    getEmailStatus({
      email: suggestion?.email,
      canBeInvitedEmails,
      existedEmails,
      pendingInvEmails,
      invalidEmails,
      existedProgramUserEmails,
      programId,
    });

  const workspace = useWorkspace();
  const user = useCurrentUser();

  const filters = buildUserGroupFilters({
    selections,
    workspaceId: workspace?.id,
    userId: user?.id,
    isWorkspaceAdmin: isWorkspaceAdmin(),
  });

  return (
    <AlgoliaMultiselect
      onChange={onChange}
      onQueryChange={onInputChange}
      placeholder={placeholder || `name@company.com`}
      filters={filters}
      indexName={[USER_INDEX, WORKSPACE_GROUP_INDEX]}
      selections={selections}
      getSuggestionValue={(suggestion) => {
        const suggestionIndexname = suggestion?._indexName;

        if (suggestionIndexname === USER_INDEX) {
          return suggestion?.email;
        } else if (suggestionIndexname === WORKSPACE_GROUP_INDEX) {
          return suggestion?.name;
        }
      }}
      renderSuggestion={(suggestion, isTag) => (
        <SuggestionItem
          suggestion={suggestion}
          isTag={isTag}
          status={getSuggestionStatus(suggestion)}
        />
      )}
      optionFromQueryPosition="prepend"
      inputStyle={inputStyle}
    />
  );
}

interface EmailStatusProps {
  canBeInvitedEmails: string[];
  existedEmails: string[];
  pendingInvEmails: string[];
  invalidEmails: string[];
  existedProgramUserEmails?: string[];
  programId?: string;
  email: string;
}

function getEmailStatus({
  email,
  canBeInvitedEmails,
  existedEmails,
  pendingInvEmails,
  invalidEmails,
  existedProgramUserEmails = [],
  programId,
}: EmailStatusProps) {
  if (programId && existedProgramUserEmails?.includes(email)) {
    return EmailStatus.existedProgramUser;
  }

  if (programId && existedEmails?.includes(email)) {
    return EmailStatus.existedButNotInProgram;
  }

  if (canBeInvitedEmails.includes(email)) {
    return EmailStatus.canBeInvited;
  }

  if (existedEmails.includes(email)) {
    return EmailStatus.existed;
  }

  if (pendingInvEmails.includes(email)) {
    return EmailStatus.pendingInv;
  }

  if (invalidEmails?.includes(email)) {
    return EmailStatus.invalid;
  }

  return undefined;
}
